import { Component, Element, h, Host, Prop, Watch } from "@stencil/core";
import pfx from "../../utils/style-class-prefix-helper";
import {
  CONTENT_POSITION_TOP,
  GRID_D10_2__T10_2,
  GRID_D12__T12,
  GRID_D2_10__T2_10,
  GRID_D2_2_2_2_2_2__T12,
  GRID_D2_2_2_2_2_2__T6_6,
  GRID_D2_4_4_2__T6_6_6_6,
  GRID_D2_5_5,
  GRID_D2_8_2,
  GRID_D3_3_3_3__T12,
  GRID_D3_3_3_3__T6_6_6_6,
  GRID_D3_3_6__T6_6_12,
  GRID_D3_6_3__T2_8_2,
  GRID_D3_9__T4_8,
  GRID_D4_4_4__T12,
  GRID_D4_4_4__T4_4_4,
  GRID_D4_8__T4_8,
  GRID_D5_5_2,
  GRID_D6_3_3__T12_6_6,
  GRID_D6_6__T6_6,
  GRID_D8_4__T8_4,
  GRID_D9_3__T8_4
} from "./constants/grid-control-constants";
// import { detectSlotChanges } from "../../utils/slot-helper";
import Fragment from "../../common/fragment";
import { ContentPosition, Grid } from "./constants/grid-control-types";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot column-1 - holds one number of eon-ui-grid-control-column.
 * @slot column-2 - holds one number of eon-ui-grid-control-column.
 * @slot column-3 - holds one number of eon-ui-grid-control-column.
 * @slot column-4 - holds one number of eon-ui-grid-control-column.
 * @slot column-5 - holds one number of eon-ui-grid-control-column.
 * @slot column-6 - holds one number of eon-ui-grid-control-column.
 */
@Component({
  tag: "eon-ui-grid-control",
  styleUrl: "grid-control.scss",
  shadow: true
})
export class GridControl {
  // @InitLogging() sentryTransaction;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiGridControlElement;

  /**
   * Defines the grid layout for desktop and tablet. Mobile always switches to components using 12 columns of the grid.
   * grid\_\_d3-9\_\_t4-8 defines a grid which is split up into two columns 3/9 on desktop (d) viewport and 4/8 on tablet (t) viewport. Mobile is always one column (12).
   * @default GRID_D12__T12;
   */
  @Prop() grid: Grid = GRID_D12__T12;

  /**
   * Defines the minimum height of the whole grid control. This is necessary if you'd like position components on a section with background image at a certain position.
   */
  @Prop() minHeight: string;

  /**
   * Sets style="height: 100%" to child's. If these support stretching according to this style setting, they will fill up the available space of the column.
   * @default false
   */
  @Prop() sameHeight: boolean = false;

  /**
   * Defines the vertical position of elements in first column.
   * @default top
   */
  @Prop() contentPositionCol1: ContentPosition = CONTENT_POSITION_TOP;

  /**
   * Defines the vertical position of elements in second column.
   * @default top
   */
  @Prop() contentPositionCol2: ContentPosition = CONTENT_POSITION_TOP;

  /**
   * Defines the vertical position of elements in third column.
   * @default top
   */
  @Prop() contentPositionCol3: ContentPosition = CONTENT_POSITION_TOP;

  /**
   * Defines the vertical position of elements in fourth column.
   * @default top
   */
  @Prop() contentPositionCol4: ContentPosition = CONTENT_POSITION_TOP;

  /**
   * Defines the vertical position of elements in fifth column.
   * @default top
   */
  @Prop() contentPositionCol5: ContentPosition = CONTENT_POSITION_TOP;

  /**
   * Defines the vertical position of elements in sixth column.
   * @default top
   */
  @Prop() contentPositionCol6: ContentPosition = CONTENT_POSITION_TOP;

  /**
   * If this is active, the phone styles are taken independently from viewport width
   */
  @Prop() forceMobileLayout: boolean = false;

  /**
   * Removes the left and right spacing between the columns.
   * @default false
   */
  @Prop({ reflect: true }) removeVerticalGutter: boolean = false;

  /**
   * Removes the horiztonal space between the components.
   * @default false
   */
  @Prop({ reflect: true }) removeHorizontalGutter: boolean = false;

  /**
   * Used by section component to remove the last gutter spacing.
   * @default false
   */
  @Prop({ reflect: true }) removeBottomSpacings: boolean = false;

  componentWillLoad() {
    this.toggleHeight();
    this.handleHorizontalGutterForComponents();
  }

  @Watch("removeHorizontalGutter")
  handleHorizontalGutterForComponents() {
    if (!!this.removeHorizontalGutter) {
      for (let child of Array.from(this.hostElement.children) as HTMLElement[]) {
        child.setAttribute("data-remove-horizontal-gutter", "true");
      }
    } else {
      for (let child of Array.from(this.hostElement.children) as HTMLElement[]) {
        child.removeAttribute("data-remove-horizontal-gutter");
      }
    }
  }

  @Watch("sameHeight")
  private toggleHeight() {
    for (let child of Array.from(this.hostElement.children) as HTMLElement[]) {
      this.sameHeight ? (child.style.height = "100%") : (child.style.height = "");
      /**
       * if there is only 1 child then it will automatically be the last child,
       * if more than 1 children then only the last child will get the height as 100%
       */
      const gridColumnChildren = Array.from(child.children);
      if (gridColumnChildren && gridColumnChildren.length) {
        if (gridColumnChildren.length > 1) {
          const firstChild = gridColumnChildren[0] as HTMLElement;
          this.sameHeight ? (firstChild.style.height = "fit-content") : (firstChild.style.height = "");
        }
        const lastChild = gridColumnChildren[gridColumnChildren.length - 1] as HTMLElement;
        this.sameHeight ? lastChild?.style?.setProperty("height", "100%") : lastChild?.style?.removeProperty("height");
      }
    }
  }

  private renderGrid() {
    switch (this.grid) {
      case GRID_D12__T12:
        return (
          <div
            class={pfx({
              "grid-column": true,
              "grid-column-12": true,
              [this.contentPositionCol1]: this.contentPositionCol1
            })}
          >
            <slot name="column-1" />
          </div>
        );
      case GRID_D2_10__T2_10:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-10": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
          </Fragment>
        );
      case GRID_D3_9__T4_8:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-9": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
          </Fragment>
        );
      case GRID_D4_8__T4_8:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-8": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
          </Fragment>
        );
      case GRID_D6_6__T6_6:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-6": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-6": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
          </Fragment>
        );
      case GRID_D8_4__T8_4:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-8": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
          </Fragment>
        );
      case GRID_D9_3__T8_4:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-9": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
          </Fragment>
        );
      case GRID_D10_2__T10_2:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-10": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
          </Fragment>
        );
      case GRID_D2_5_5:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-5": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-5": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
          </Fragment>
        );
      case GRID_D2_8_2:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-8": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
          </Fragment>
        );
      case GRID_D3_3_6__T6_6_12:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-6": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
          </Fragment>
        );
      case GRID_D3_6_3__T2_8_2:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-8": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
          </Fragment>
        );
      case GRID_D4_4_4__T4_4_4:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
          </Fragment>
        );
      case GRID_D5_5_2:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-5": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-5": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
          </Fragment>
        );
      case GRID_D6_3_3__T12_6_6:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-6": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
          </Fragment>
        );
      case GRID_D2_4_4_2__T6_6_6_6:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol4]: this.contentPositionCol4
              })}
            >
              <slot name="column-4" />
            </div>
          </Fragment>
        );
      case GRID_D3_3_3_3__T6_6_6_6:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol4]: this.contentPositionCol4
              })}
            >
              <slot name="column-4" />
            </div>
          </Fragment>
        );
      case GRID_D2_2_2_2_2_2__T6_6:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol4]: this.contentPositionCol4
              })}
            >
              <slot name="column-4" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol5]: this.contentPositionCol5
              })}
            >
              <slot name="column-5" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol6]: this.contentPositionCol6
              })}
            >
              <slot name="column-6" />
            </div>
          </Fragment>
        );
      case GRID_D3_3_3_3__T12:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-3": true,
                [this.contentPositionCol4]: this.contentPositionCol4
              })}
            >
              <slot name="column-4" />
            </div>
          </Fragment>
        );
      case GRID_D4_4_4__T12:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-4": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
          </Fragment>
        );
      case GRID_D2_2_2_2_2_2__T12:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol2]: this.contentPositionCol2
              })}
            >
              <slot name="column-2" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol3]: this.contentPositionCol3
              })}
            >
              <slot name="column-3" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol4]: this.contentPositionCol4
              })}
            >
              <slot name="column-4" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol5]: this.contentPositionCol5
              })}
            >
              <slot name="column-5" />
            </div>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-2": true,
                [this.contentPositionCol6]: this.contentPositionCol6
              })}
            >
              <slot name="column-6" />
            </div>
          </Fragment>
        );
      default:
        return (
          <Fragment>
            <div
              class={pfx({
                "grid-column": true,
                "grid-column-12": true,
                [this.contentPositionCol1]: this.contentPositionCol1
              })}
            >
              <slot name="column-1" />
            </div>
          </Fragment>
        );
    }
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "grid-container": true,
            [this.grid]: this.grid,
            "remove-vertical-gutter": this.removeVerticalGutter,
            "remove-horizontal-gutter": this.removeHorizontalGutter,
            "same-height": this.sameHeight,
            "force-mobile-layout": !!this.forceMobileLayout
          })}
          style={{
            "min-height": this.minHeight
          }}
        >
          {this.renderGrid()}
        </div>
      </Host>
    );
  }
}
